import React, { Fragment } from "react";

import { truncate } from "lodash";

import useGetPortfolioPlanById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanById";
import useGetProposal from "@fartherfinance/frontend/api/Investment/hooks/useGetProposal";
import {
  RebalancerWithMetadata,
  TradeProposalId,
  TransformerId,
} from "@fartherfinance/frontend/api/Investment/types";
import {
  PortfolioPlanAssignmentId,
  PortfolioPlanId,
  SecurityId,
} from "@fartherfinance/frontend/api/Types";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import Card from "@src/sharedComponents/Card/Card";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./ProposalDetails.module.css";

interface Props {
  portfolioPlanAssignmentId: PortfolioPlanAssignmentId | undefined; // undefined means loading
  portfolioPlanId: PortfolioPlanId | undefined;
  proposalId: TradeProposalId | undefined;
  proposalCreatedAt: Date | undefined; // Optional proposal creation date
  filteredExcludedAssets?: Array<{ securityId: SecurityId; createdAt?: Date }>;
  equivalentSecuritiesEnabled?: boolean;
}

function isTlhOn(rebalancers: RebalancerWithMetadata[]): boolean {
  return rebalancers.some(
    (x) => x.rebalancer === "TaxLossHarvestingOnlyRebalancer"
  );
}

const CardPortfolioPlanAssignment: React.FC<Props> = (props) => {
  const auth = useAdvisorRequestAuth();
  const trade = useGetProposal(props.proposalId ?? null, auth);

  const portfolioPlan = useGetPortfolioPlanById(
    props.portfolioPlanId ?? null,
    {
      $expand: ["numberOfSecurities"],
    },
    auth
  );

  const portfolioName =
    portfolioPlan.data === undefined
      ? undefined
      : truncate(portfolioPlan.data.data.plan.name, { length: 20 });

  return (
    <Card header="Target Portfolio">
      <Tooltip tooltipText={portfolioPlan.data?.data.plan.name ?? "Loading"}>
        <div className={styles.FlexRow}>
          <Text variant="bodyBold">Portfolio</Text>
          <Text variant="bodyBold">:&nbsp;</Text>

          <Text variant="body">
            {portfolioName ?? <Skeleton width={100} />}
          </Text>
        </div>
      </Tooltip>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Id</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {props.portfolioPlanAssignmentId !== undefined ? (
            <Tooltip
              tooltipText={props.portfolioPlanAssignmentId}
              placement="top"
            >
              <Text variant="body">
                {truncate(props.portfolioPlanAssignmentId, { length: 20 })}
              </Text>
            </Tooltip>
          ) : (
            <Skeleton width={100} />
          )}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Updated</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {portfolioPlan.data === undefined ? (
            <Skeleton width={30} />
          ) : (
            `${portfolioPlan.data.data.plan.updatedAt.toLocaleString("en-US", {
              timeZone: "America/New_York",
              dateStyle: "short",
              timeStyle: "short",
            })} (East)`
          )}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Plan Version</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {portfolioPlan.data?.data.plan.version ?? <Skeleton width={30} />}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Provider</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {portfolioPlan.data === undefined ? (
            <Skeleton width={30} />
          ) : (
            portfolioPlan.data.data.plan.provider ?? "Unknown"
          )}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Tags</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {portfolioPlan.data === undefined ? (
            <Skeleton width={30} />
          ) : (
            portfolioPlan.data.data.plan.tags.join(", ")
          )}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Equivalent Securities</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {props.equivalentSecuritiesEnabled === true
            ? "Yes"
            : props.equivalentSecuritiesEnabled === false
            ? "No"
            : "Unknown"}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Tax-loss harvesting</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {trade.data?.portfolioPlanAssignment === undefined ? (
            <Skeleton width={30} />
          ) : isTlhOn(trade.data.portfolioPlanAssignment.rebalancers) ? (
            "Yes"
          ) : (
            "No"
          )}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Do Not Sell</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {props.filteredExcludedAssets ? (
            props.filteredExcludedAssets.length > 0 ? (
              "Yes"
            ) : (
              "No"
            )
          ) : (
            <Skeleton width={30} />
          )}
        </Text>
      </div>

      <div className={styles.VerticalSeparator} />

      {(portfolioPlan.data?.data.plan.transformers ?? [undefined]).map(
        (rebalancer, idx) => (
          <Fragment key={`${rebalancer?.transformerId ?? ""}-${idx}`}>
            {idx > 0 && <div className={styles.Divider} />}

            <TransformerSection
              location="Plan"
              transformerId={rebalancer?.transformerId}
              metadata={rebalancer?.metadata}
            />
          </Fragment>
        )
      )}

      {(trade.data?.portfolioPlanAssignment.transformers ?? [undefined]).map(
        (rebalancer, idx) => (
          <Fragment key={`${rebalancer?.transformerId ?? ""}-${idx}`}>
            {idx > 0 && <div className={styles.Divider} />}

            <TransformerSection
              location="Assignment"
              transformerId={rebalancer?.transformerId}
              metadata={rebalancer?.metadata}
            />
          </Fragment>
        )
      )}
    </Card>
  );
};

export default CardPortfolioPlanAssignment;

interface TransformerSectionProps {
  location: "Plan" | "Assignment";
  transformerId: TransformerId | undefined;
  metadata: any;
}

const TransformerSection: React.FC<TransformerSectionProps> = (props) => {
  if (props.transformerId === undefined) {
    return (
      <div className={styles.FlexRow}>
        <Text variant="bodyBold">{props.location} Transformer</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          <Skeleton width={50} />
        </Text>
      </div>
    );
  }

  return (
    <>
      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Transformer</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">{props.transformerId}</Text>
      </div>
    </>
  );
};
